import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  FinSantanderApiService,
  SalvarSantanderApiConfigPayload,
  SantanderApiConfigView,
  SantanderWebhookLogItem
} from 'src/app/service/fin-santander-api.service';
import { FinSantanderCnabService } from 'src/app/service/fin-santander-cnab.service';
import { UtilService } from 'src/app/service/util.service';

@Component({
  selector: 'app-configuracao-cnab-santander',
  templateUrl: './configuracao-cnab-santander.component.html',
  styleUrls: ['./configuracao-cnab-santander.component.scss']
})
export class ConfiguracaoCnabSantanderComponent implements OnInit {
  carregando = false;
  salvandoConfig = false;
  enviandoCertificado = false;
  testandoConexao = false;
  criandoWorkspace = false;
  configurandoWebhook = false;
  carregandoWebhookLogs = false;

  clientSecretConfigurado = false;
  webhookUrlSugerida = '';
  webhookLogs: SantanderWebhookLogItem[] = [];
  certificadoConfigurado = false;
  certificadoArquivo = '';
  workspaceId = '';

  certificadoSenha = '';
  arquivoCertificado: File | null = null;
  apiClientId = '';
  apiClientSecret = '';

  configForm: SalvarSantanderApiConfigPayload = {
    descricao: 'Banco Santander S.A.',
    agencia: '',
    digitoAgencia: '',
    contaCorrente: '',
    digitoConta: '',
    nomeBeneficiarioCobranca: '',
    apiClientId: '',
    apiClientSecret: '',
    apiAmbiente: 'S',
    codigoConvenio: '',
    webhookUrl: '',
    webhookBoletoAtivo: true,
    webhookPixAtivo: true,
    apiMultaPercentual: null,
    apiJurosPercentualMensal: null,
    apiMultaDiasAposVencimento: 0,
  };

  constructor(
    private santanderApi: FinSantanderApiService,
    private santanderCnab: FinSantanderCnabService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.carregarConfig();
    this.carregarWebhookLogs();
  }

  carregarConfig(): void {
    this.carregando = true;
    this.util.showLoading();
    forkJoin({
      api: this.santanderApi.obterConfig().pipe(catchError(() => of(null))),
      cnab: this.santanderCnab.obterConfig().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ api, cnab }) => {
        this.util.closeLoading();
        this.carregando = false;
        const cfg = this.mesclarConfig(api, cnab);
        if (!cfg) {
          const registros = this.extrairRegistrosDiagnostico(api, cnab);
          const msg = registros.length
            ? `Nenhuma config Santander (033) encontrada. Registros no banco: ${registros
                .map((r) => `empresa ${r.idEmpresa} banco ${r.codigoBanco}`)
                .join(', ')}.`
            : 'Nenhuma configuração Santander encontrada. Preencha os dados e salve novamente.';
          this.util.toasterShowWarning('Configuração Santander', msg);
          return;
        }

        if (!this.apiConfigurada(api) && cnab?.configurado) {
          this.util.toasterShowWarning(
            'Configuração API',
            'Dados da conta carregados (CNAB). Credenciais da API devem vir de SantanderApi — reinicie a API se Client ID/workspace não aparecerem.'
          );
        }

        this.clientSecretConfigurado = cfg.clientSecretConfigurado;
        this.certificadoConfigurado = cfg.certificadoConfigurado;
        this.certificadoArquivo = cfg.certificadoArquivo;
        this.workspaceId = cfg.workspaceId || '';
        this.apiClientId = cfg.apiClientId || '';
        this.webhookUrlSugerida = cfg.webhookUrlSugerida || '';

        if (!this.workspaceId && cfg.registrosNoBanco?.length) {
          const diag = cfg.registrosNoBanco.find(
            (r) => r.workspaceId || r.apiClientId || (r.codigoBanco === '033' && r.temWorkspace)
          );
          if (diag?.workspaceId) {
            this.workspaceId = diag.workspaceId;
          }
        }

        if (!this.apiClientId && cfg.registrosNoBanco?.length) {
          const diag = cfg.registrosNoBanco.find((r) => r.apiClientId);
          if (diag?.apiClientId) {
            this.apiClientId = diag.apiClientId;
          }
        }

        if (!this.certificadoConfigurado && cfg.registrosNoBanco?.length) {
          const diag = cfg.registrosNoBanco.find((r) => r.certificadoArquivo || r.temCertificado);
          if (diag?.certificadoArquivo) {
            this.certificadoArquivo = diag.certificadoArquivo;
            this.certificadoConfigurado = true;
          }
        }

        this.configForm.descricao = cfg.descricao || this.configForm.descricao || 'Banco Santander S.A.';
        this.configForm.agencia = cfg.agencia || this.configForm.agencia;
        this.configForm.digitoAgencia = cfg.digitoAgencia || this.configForm.digitoAgencia;
        this.configForm.contaCorrente = cfg.contaCorrente || this.configForm.contaCorrente;
        this.configForm.digitoConta = cfg.digitoConta || this.configForm.digitoConta;
        this.configForm.nomeBeneficiarioCobranca =
          cfg.nomeBeneficiarioCobranca || this.configForm.nomeBeneficiarioCobranca;
        this.configForm.apiAmbiente = cfg.apiAmbiente || this.configForm.apiAmbiente;
        this.configForm.codigoConvenio = cfg.codigoConvenio || this.configForm.codigoConvenio;
        this.configForm.webhookUrl = cfg.webhookUrl || cfg.webhookUrlSugerida || this.configForm.webhookUrl;
        if (!this.configForm.webhookUrl && cfg.registrosNoBanco?.length) {
          const diag = cfg.registrosNoBanco.find((r) => r.webhookUrl);
          if (diag?.webhookUrl) {
            this.configForm.webhookUrl = diag.webhookUrl;
          }
        }
        this.configForm.webhookBoletoAtivo = cfg.webhookBoletoAtivo;
        this.configForm.webhookPixAtivo = cfg.webhookPixAtivo;
        this.configForm.apiMultaPercentual = cfg.apiMultaPercentual ?? null;
        this.configForm.apiJurosPercentualMensal = cfg.apiJurosPercentualMensal ?? null;
        this.configForm.apiMultaDiasAposVencimento = cfg.apiMultaDiasAposVencimento ?? 0;
      },
      error: () => {
        this.util.closeLoading();
        this.carregando = false;
        this.util.toasterShowError('Erro', 'Não foi possível carregar a configuração.');
      }
    });
  }

  private mesclarConfig(api: SantanderApiConfigView | null, cnab: any): SantanderApiConfigView | null {
    const apiCfg = api || ({} as SantanderApiConfigView);
    const cnabCfg = cnab || {};
    const temApi = this.apiConfigurada(api);
    const temCnab = Boolean(cnab?.configurado);

    if (!temApi && !temCnab) {
      return null;
    }

    const codigoConvenioApi = temApi ? String(apiCfg.codigoConvenio || '').trim() : '';
    const codigoTransmissaoCnab = String(cnabCfg.codigoTransmissao || '').trim();
    const convenioDerivado =
      codigoConvenioApi ||
      (codigoTransmissaoCnab.length >= 7 ? codigoTransmissaoCnab.substring(codigoTransmissaoCnab.length - 7) : '');

    return {
      configurado: temApi || temCnab,
      descricao: apiCfg.descricao || cnabCfg.descricao || 'Banco Santander S.A.',
      agencia: apiCfg.agencia || cnabCfg.agencia || '',
      digitoAgencia: apiCfg.digitoAgencia || cnabCfg.digitoAgencia || '',
      contaCorrente: apiCfg.contaCorrente || cnabCfg.contaCorrente || '',
      digitoConta: apiCfg.digitoConta || cnabCfg.digitoConta || '',
      nomeBeneficiarioCobranca: apiCfg.nomeBeneficiarioCobranca || cnabCfg.nomeBeneficiarioCobranca || '',
      apiClientId: temApi ? (apiCfg.apiClientId || '') : '',
      clientSecretConfigurado: temApi ? Boolean(apiCfg.clientSecretConfigurado) : false,
      apiAmbiente: temApi ? (apiCfg.apiAmbiente || 'S') : 'S',
      workspaceId: temApi ? (apiCfg.workspaceId || '') : '',
      codigoConvenio: convenioDerivado,
      certificadoConfigurado: temApi ? Boolean(apiCfg.certificadoConfigurado) : false,
      certificadoArquivo: temApi ? (apiCfg.certificadoArquivo || '') : '',
      webhookUrl: temApi ? (apiCfg.webhookUrl || '') : '',
      webhookBoletoAtivo: temApi ? (apiCfg.webhookBoletoAtivo ?? true) : true,
      webhookPixAtivo: temApi ? (apiCfg.webhookPixAtivo ?? true) : true,
      webhookUrlSugerida: temApi ? (apiCfg.webhookUrlSugerida || '') : '',
      apiMultaPercentual: temApi ? (apiCfg.apiMultaPercentual ?? null) : null,
      apiJurosPercentualMensal: temApi ? (apiCfg.apiJurosPercentualMensal ?? null) : null,
      apiMultaDiasAposVencimento: temApi ? (apiCfg.apiMultaDiasAposVencimento ?? null) : null,
      registrosNoBanco: apiCfg.registrosNoBanco || [],
    };
  }

  /** Credenciais/workspace vêm exclusivamente de SantanderApi/Config. */
  private apiConfigurada(api: SantanderApiConfigView | null): boolean {
    if (!api) {
      return false;
    }
    return Boolean(
      api.configurado ||
        api.apiClientId ||
        api.workspaceId ||
        api.certificadoConfigurado ||
        api.clientSecretConfigurado
    );
  }

  private extrairRegistrosDiagnostico(
    api: SantanderApiConfigView | null,
    cnab: any
  ): { idEmpresa: number; codigoBanco: string }[] {
    const lista = api?.registrosNoBanco || [];
    if (lista.length) {
      return lista.map((r) => ({ idEmpresa: r.idEmpresa, codigoBanco: r.codigoBanco }));
    }
    if (cnab?.registrosNoBanco?.length) {
      return cnab.registrosNoBanco.map((r: any) => ({
        idEmpresa: Number(r.idEmpresa ?? r.idempresa ?? 0),
        codigoBanco: String(r.codigoBanco ?? r.codigobanco ?? ''),
      }));
    }
    return [];
  }

  salvarConfiguracao(): void {
    const clientId = String(this.apiClientId || this.configForm.apiClientId || '').trim();
    const clientSecret = String(this.apiClientSecret || this.configForm.apiClientSecret || '').trim();
    const p = { ...this.configForm, apiClientId: clientId, apiClientSecret: clientSecret };
    if (!String(p.codigoConvenio || '').trim()) {
      this.util.toasterShowWarning('Configuração', 'Código de convênio é obrigatório.');
      return;
    }
    if (!clientId) {
      this.util.toasterShowWarning('Configuração', 'Client ID é obrigatório.');
      return;
    }
    if (!this.clientSecretConfigurado && !clientSecret) {
      this.util.toasterShowWarning('Configuração', 'Client Secret é obrigatório na primeira configuração.');
      return;
    }

    this.salvandoConfig = true;
    this.util.showLoading();
    this.santanderApi.salvarConfig(p).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.salvandoConfig = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Configuração', response.message || 'Não foi possível salvar.');
          return;
        }
        this.util.toasterShowSuccess('Configuração', 'Dados salvos com sucesso.');
        this.apiClientSecret = '';
        this.carregarConfig();
      },
      error: () => {
        this.util.closeLoading();
        this.salvandoConfig = false;
        this.util.toasterShowError('Erro', 'Falha ao salvar configuração.');
      }
    });
  }

  onCertificadoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.arquivoCertificado = input.files?.length ? input.files[0] : null;
  }

  enviarCertificado(): void {
    if (!this.arquivoCertificado) {
      return;
    }
    this.enviandoCertificado = true;
    this.util.showLoading();
    this.santanderApi.uploadCertificado(this.arquivoCertificado, this.certificadoSenha).subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.enviandoCertificado = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Certificado', response.message || 'Não foi possível enviar o certificado.');
          return;
        }
        this.util.toasterShowSuccess('Certificado', 'Certificado enviado com sucesso.');
        this.arquivoCertificado = null;
        this.carregarConfig();
      },
      error: () => {
        this.util.closeLoading();
        this.enviandoCertificado = false;
        this.util.toasterShowError('Erro', 'Falha ao enviar certificado.');
      }
    });
  }

  testarConexao(): void {
    this.testandoConexao = true;
    this.util.showLoading();
    this.santanderApi.testarConexao().subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.testandoConexao = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Conexão', response.message || 'Falha ao testar conexão.');
          return;
        }
        const msg = response?.data?.mensagem || response?.message || 'Conexão OK.';
        this.util.toasterShowSuccess('Conexão', msg);
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.testandoConexao = false;
        this.util.toasterShowError('Conexão', err?.error?.message || err?.message || 'Falha ao testar conexão.');
      }
    });
  }

  carregarWebhookLogs(): void {
    this.carregandoWebhookLogs = true;
    this.santanderApi.listarWebhookLogs(20).subscribe({
      next: (logs) => {
        this.webhookLogs = logs;
        this.carregandoWebhookLogs = false;
      },
      error: () => {
        this.webhookLogs = [];
        this.carregandoWebhookLogs = false;
      }
    });
  }

  usarUrlWebhookSugerida(): void {
    if (this.webhookUrlSugerida) {
      this.configForm.webhookUrl = this.webhookUrlSugerida;
    }
  }

  configurarWebhook(): void {
    if (!String(this.configForm.webhookUrl || '').trim()) {
      this.util.toasterShowWarning('Webhook', 'Informe a URL do webhook e salve a configuração.');
      return;
    }
    if (!this.workspaceId) {
      this.util.toasterShowWarning('Webhook', 'Crie a workspace antes de configurar o webhook.');
      return;
    }

    this.configurandoWebhook = true;
    this.util.showLoading();
    this.santanderApi.configurarWebhook().subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.configurandoWebhook = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Webhook', response.message || 'Não foi possível configurar o webhook.');
          return;
        }
        const msg = response?.data?.mensagem || response?.message || 'Webhook configurado.';
        this.util.toasterShowSuccess('Webhook', msg);
        this.carregarWebhookLogs();
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.configurandoWebhook = false;
        this.util.toasterShowError('Webhook', err?.error?.message || err?.message || 'Falha ao configurar webhook.');
      }
    });
  }

  criarWorkspace(): void {
    this.criandoWorkspace = true;
    this.util.showLoading();
    this.santanderApi.criarWorkspace().subscribe({
      next: (response: any) => {
        this.util.closeLoading();
        this.criandoWorkspace = false;
        if (response?.hasErrors) {
          this.util.toasterShowError('Workspace', response.message || 'Não foi possível criar a workspace.');
          return;
        }
        this.util.toasterShowSuccess('Workspace', 'Workspace criada com sucesso.');
        this.carregarConfig();
      },
      error: (err: any) => {
        this.util.closeLoading();
        this.criandoWorkspace = false;
        this.util.toasterShowError('Workspace', err?.error?.message || err?.message || 'Falha ao criar workspace.');
      }
    });
  }
}
